import React from 'react';
import { Mail, Linkedin } from 'lucide-react';
import gracePortrait from '@/assets/partner-grace-gkii.jpg';
import SEO from '../../components/SEO';

const GraceGakii = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Grace Gakii — Law and Code Africa"
        description="Grace Gakii at Law and Code Africa. LLB Hons, CPM, Trainee Advocate. Focused on digital & tech law, litigation, and legal research."
        path="/partners/grace-gakii"
      />

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-primary">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Grace Gakii</h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto mb-8">
              LLB Hons | CPM | Trainee Advocate
            </p>
            <div className="max-w-6xl mx-auto text-lg text-blue-100 space-y-4 text-justify px-4">
              <p>
                Grace Gakii is a motivated and detail-oriented legal mind with primary areas of interest in Digital &amp; Tech Law, Litigation, and Legal Research. Certified as a Professional Mediator and currently an Advocates Training Programme (ATP) student at the Kenya School of Law, she is committed to a career that combines rigorous legal advocacy with constructive dialogue.
              </p>
              <p>
                She has demonstrated the ability to analyze complex legal issues, facilitate conflict resolution, and engage effectively with diverse stakeholders through workshops, webinars, public lectures, and active professional networking.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 mb-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center">
              <div className="relative overflow-hidden rounded-lg shadow-lg w-full max-w-sm mb-6 bg-gray-50">
                <img
                  src={gracePortrait}
                  alt="Grace Gakii - Partner, Law and Code Africa"
                  loading="lazy"
                  className="w-full h-auto object-cover transition-all duration-700 hover:scale-110 hover:brightness-110"
                />
              </div>
              <div className="bg-gray-50 p-5 md:p-6 rounded-lg w-full max-w-sm">
                <h3 className="text-lg md:text-xl font-bold text-primary mb-4 text-center">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail className="text-secondary flex-shrink-0" size={20} />
                    <span className="text-gray-700 text-sm md:text-base break-all">lawandcodeafrica@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3 min-w-0">
                    <Linkedin className="text-secondary flex-shrink-0" size={20} />
                    <a
                      href="https://www.linkedin.com/in/grace-gakii-761475238"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm md:text-base break-all"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-primary mb-8">Background and Qualifications</h2>
            <div className="space-y-6 text-gray-700">
              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Educational Foundation</h3>
                <div className="space-y-3">
                  <p>
                    <strong>JKUAT School of Law (LLB Hons):</strong> Attended Jomo Kenyatta University of Agriculture and Technology. During her tenure, she was a founding member of the JKUAT Law Review, Vice-Chairperson of the JKUAT Mooting Club, and a founding member of the Amnesty JKUAT Chapter.
                  </p>
                  <p>
                    <strong>Kenya School of Law:</strong> Currently an Advocates Training Programme (ATP) student.
                  </p>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Qualifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Certified Professional Mediator</h4>
                    <p className="text-sm text-gray-600">Holds certification as a Professional Mediator</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Legal Researcher</h4>
                    <p className="text-sm text-gray-600">Skilled in legal research, drafting, and document review</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-secondary mb-2">Public Speaker</h4>
                    <p className="text-sm text-gray-600">Active in workshops, webinars, and public lectures</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-l-4 border-secondary p-6 rounded-r-lg shadow-sm">
                <h3 className="text-xl font-semibold text-primary mb-3">Professional Experience &amp; Community Involvement</h3>
                <div className="space-y-3">
                  <p>
                    <strong>McKay &amp; Company LLP:</strong> Completed internship, gaining practical legal experience across various practice areas.
                  </p>
                  <p>
                    <strong>Waraka Initiative:</strong> Volunteers as a Researcher, contributing articles focused on human rights and participating in workshops and webinars.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Areas of Specialization */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary mb-8 text-center">
            Key Practice Areas and Interests
          </h2>
          <p className="text-lg text-gray-600 text-center mb-10 max-w-4xl mx-auto">
            Grace Gakii's professional interests and developing specializations encompass:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Digital &amp; Tech Law</h3>
              <p className="text-gray-600">
                Emerging legal frameworks governing digital platforms, data protection, online conduct, and the broader technology ecosystem.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Litigation</h3>
              <p className="text-gray-600">
                Civil and commercial litigation support, drafting of pleadings, case management, and observation of court proceedings and filing processes.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-secondary">
              <h3 className="text-xl font-semibold text-primary mb-3">Legal Research</h3>
              <p className="text-gray-600">
                Rigorous research and analysis on complex legal issues, statutory interpretation, and commentary on Parliamentary Bills and policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraceGakii;
